import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import {
  Button,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
} from '@monksflow/monks-ui';
import { ArrowLeftRightIcon, LockKeyholeIcon, SearchXIcon } from 'lucide-react';
import { useCurrentOrganizationName } from '@/entities/user';
import { ROUTES } from '@/shared/config/routes';
import type { TerminalResolutionState } from '@/shared/hooks/useDeepLinkResolution';
import { EmptyStateTitle } from '@/shared/ui';
import { canNavigateBack } from '@/shared/utils/canNavigateBack';
import { Trans, useTranslation } from 'react-i18next';

/**
 * Which entity the deep link pointed at. It selects an i18next context, so each
 * locale writes the whole sentence: Spanish and Portuguese inflect the article
 * and demonstrative for gender, and the gender of one entity differs between
 * them (`el análisis` but `a análise`), so it cannot be derived here.
 */
export type DeepLinkEntity =
  | 'analysis'
  | 'briefTemplate'
  | 'contentTest'
  | 'focusGroup'
  | 'guidelinesFolder'
  | 'project';

interface Props {
  state: TerminalResolutionState;
  entity: DeepLinkEntity;
}

// Mirrors the Projects page empty state (`ProjectsViewState`): `Empty` owns
// centering and sizing itself — wrapping it in another flex container
// collapses the text column to min-content width. `h-full` (rather than the
// reference's `flex-1`) because the entity route's container isn't a flex
// parent; Empty's own justify-center then centers the content vertically.
function InterstitialSurface(props: { testId: string; children: ReactNode }) {
  return (
    <Empty role="status" data-testid={props.testId} className="h-full flex-1">
      {props.children}
    </Empty>
  );
}

/** Terminal outcome (no recovery action): access-denied and not-available. */
function TerminalState(props: {
  testId: string;
  icon: ReactNode;
  title: string;
  description: string;
}) {
  const { t } = useTranslation('common');

  const navigate = useNavigate();
  // On a pasted deep link / fresh tab there is nothing to go back to, so the
  // action lands on Home — and the label says so. Only offer "Back" when it
  // genuinely returns to the prior in-app view.
  const canGoBack = canNavigateBack();

  return (
    <InterstitialSurface testId={props.testId}>
      <EmptyHeader>
        <EmptyMedia variant="icon">{props.icon}</EmptyMedia>
        <EmptyStateTitle>{props.title}</EmptyStateTitle>
        <EmptyDescription>{props.description}</EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <Button
          variant="outline"
          onClick={() => void (canGoBack ? navigate(-1) : navigate(ROUTES.HOME))}
        >
          {canGoBack
            ? t('components.deepLinkInterstitial.back')
            : t('components.deepLinkInterstitial.goToHome')}
        </Button>
      </EmptyContent>
    </InterstitialSurface>
  );
}

/**
 * Resolution surface for the entity deep-link seam (`useDeepLinkResolution`).
 * Renders ON the entity route itself (a render-state of the page, not a
 * separate `/resolve` route) so the address bar keeps the pasted URL — it
 * survives refresh and stays copyable.
 *
 * Cross-org is the only actionable state: the app must never switch orgs
 * silently (the switch rotates the shared httpOnly session cookie and flips
 * every open tab), so it prompts before bouncing through the Platform
 * re-auth flow. Access-denied and the generic not-available are terminal;
 * non-member and nonexistent ids share the identical generic state on
 * purpose so the surface doesn't leak which one it was.
 */
export function DeepLinkInterstitial(props: Props) {
  const { t } = useTranslation('common');

  const { state, entity } = props;
  const currentOrganizationName = useCurrentOrganizationName();

  if (state.status === 'cross-org') {
    const { organizationName } = state.resolution;
    return (
      <InterstitialSurface testId="resolution-cross-org">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <ArrowLeftRightIcon />
          </EmptyMedia>
          <EmptyStateTitle>
            <Trans
              i18nKey="components.deepLinkInterstitial.thisEntityIsIn"
              ns="common"
              context={entity}
              values={{ organizationName }}
              components={{ org: <span className="font-semibold" /> }}
            />
          </EmptyStateTitle>
          <EmptyDescription>
            {t('components.deepLinkInterstitial.openingItSignsYouInto', { organizationName })}
          </EmptyDescription>
        </EmptyHeader>
        <EmptyContent className="flex-row justify-center gap-3">
          <Button onClick={state.switchAndOpen}>
            {t('components.deepLinkInterstitial.switchOpen')}
          </Button>
          <Button variant="outline" onClick={state.cancel}>
            {state.canGoBack
              ? t('components.deepLinkInterstitial.cancel')
              : t('components.deepLinkInterstitial.stayIn', {
                  organizationName:
                    currentOrganizationName ??
                    t('components.deepLinkInterstitial.currentOrganization'),
                })}
          </Button>
        </EmptyContent>
      </InterstitialSurface>
    );
  }

  if (state.status === 'access-denied') {
    return (
      <TerminalState
        testId="resolution-access-denied"
        icon={<LockKeyholeIcon />}
        title={t('components.deepLinkInterstitial.youDonTHaveAccess', { context: entity })}
        description={t('components.deepLinkInterstitial.askAMemberToInvite', { context: entity })}
      />
    );
  }

  return (
    <TerminalState
      testId="resolution-not-available"
      icon={<SearchXIcon />}
      title={t('components.deepLinkInterstitial.notAvailable', { context: entity })}
      description={t('components.deepLinkInterstitial.weCouldnTLoadThe', { context: entity })}
    />
  );
}
